/**
 * Enhanced NotificationsSection Component
 *
 * Manage notification preferences:
 * - Email notifications (order updates, messages)
 * - Marketing emails (PIPEDA: can be withdrawn)
 * - Push notifications (if enabled)
 */

'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Bell, Mail, Tag, Info } from 'lucide-react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { toast } from 'sonner';
import { exportUserData, type ExportUserDataResponse } from '../../api';
import { cn } from '@/src/shared/lib/utils';

export function EnhancedNotificationsSection() {
  const t = useTranslations('settings.notifications');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState<ExportUserDataResponse | null>(null);

  // Notification preferences
  const [preferences, setPreferences] = useState({
    orderUpdates: true, // Essential - always enabled
    messageNotifications: true,
    reviewReminders: true,
    marketingEmails: false, // PIPEDA: Can be withdrawn
    systemAnnouncements: true,
  });

  // Load user data on mount
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const data = await exportUserData();
      setUserData(data);

      // Set marketing consent from user data
      setPreferences((prev) => ({
        ...prev,
        marketingEmails: data.userData.marketingConsent,
      }));
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: Implement proper notification preferences API endpoint
      // For now, this is a placeholder

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success(t('saveSuccess'));

      // Reload user data
      await loadUserData();
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.error(t('saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Page Header */}
      <div>
        <Typography variant="h2" className="mb-2">
          {t('title')}
        </Typography>
        <Typography variant="bodySm" color="secondary">
          {t('description')}
        </Typography>
      </div>

      {/* Email Notifications Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('email.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('email.description')}
        </Typography>

        <div className="space-y-3">
          {/* Order Updates - Essential */}
          <NotificationToggle
            label={t('email.orderUpdates.title')}
            description={t('email.orderUpdates.description')}
            checked={preferences.orderUpdates}
            onChange={(checked) => setPreferences({ ...preferences, orderUpdates: checked })}
            disabled={true}
            essential={true}
          />

          {/* Message Notifications */}
          <NotificationToggle
            label={t('email.messages.title')}
            description={t('email.messages.description')}
            checked={preferences.messageNotifications}
            onChange={(checked) => setPreferences({ ...preferences, messageNotifications: checked })}
          />

          {/* Review Reminders */}
          <NotificationToggle
            label={t('email.reviews.title')}
            description={t('email.reviews.description')}
            checked={preferences.reviewReminders}
            onChange={(checked) => setPreferences({ ...preferences, reviewReminders: checked })}
          />

          {/* System Announcements */}
          <NotificationToggle
            label={t('email.system.title')}
            description={t('email.system.description')}
            checked={preferences.systemAnnouncements}
            onChange={(checked) => setPreferences({ ...preferences, systemAnnouncements: checked })}
          />
        </div>
      </section>

      {/* Marketing Emails Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('marketing.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('marketing.description')}
        </Typography>

        {/* PIPEDA Notice */}
        <div className="bg-accent-primary/10 border border-accent-primary/20 rounded-lg p-4 flex gap-3 mb-4">
          <Info className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
          <div>
            <Typography variant="bodyMd" weight="semibold" className="mb-1">
              {t('marketing.pipedaNotice.title')}
            </Typography>
            <Typography variant="bodySm" color="secondary">
              {t('marketing.pipedaNotice.description')}
            </Typography>
          </div>
        </div>

        <NotificationToggle
          label={t('marketing.promotional.title')}
          description={t('marketing.promotional.description')}
          checked={preferences.marketingEmails}
          onChange={(checked) => setPreferences({ ...preferences, marketingEmails: checked })}
          highlight={true}
        />

        {/* Show consent history if available */}
        {userData && userData.userData.marketingConsentAt && (
          <div className="bg-background-secondary rounded-lg p-3 mt-3">
            <Typography variant="bodySm" color="secondary">
              {t('marketing.consentDate', {
                date: new Date(userData.userData.marketingConsentAt).toLocaleDateString(),
              })}
            </Typography>
          </div>
        )}
      </section>

      {/* Push Notifications Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('push.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('push.description')}
        </Typography>

        <div className="bg-background-secondary border border-border-primary rounded-lg p-4">
          <Typography variant="bodyMd" color="secondary" className="text-center">
            {t('push.comingSoon')}
          </Typography>
        </div>
      </section>

      {/* Save Button */}
      <div className="pt-4 border-t border-border-primary">
        <PrimaryButton
          onClick={handleSave}
          isLoading={saving}
          className="w-full sm:w-auto"
        >
          {t('saveButton')}
        </PrimaryButton>
      </div>
    </div>
  );
}

// Helper component for notification toggle
function NotificationToggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  essential = false,
  highlight = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  essential?: boolean;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'border rounded-lg p-4 transition-colors',
        highlight && 'border-accent-primary/30 bg-accent-primary/5',
        !highlight && 'border-border-primary',
        disabled && 'bg-background-secondary opacity-70'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Typography variant="bodyMd" weight="semibold">
              {label}
            </Typography>
            {essential && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-primary/10 text-accent-primary">
                Required
              </span>
            )}
          </div>
          <Typography variant="bodySm" color="secondary">
            {description}
          </Typography>
        </div>

        {/* Toggle Switch */}
        <button
          onClick={() => !disabled && onChange(!checked)}
          disabled={disabled}
          className={cn(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2',
            checked && !disabled && 'bg-accent-primary',
            !checked && !disabled && 'bg-text-tertiary',
            disabled && 'cursor-not-allowed'
          )}
          role="switch"
          aria-checked={checked}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              checked ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </button>
      </div>
    </div>
  );
}
